import './assets/main.css';
import 'floating-vue/dist/style.css';

import { ApolloClient, createHttpLink, from, InMemoryCache } from '@apollo/client/core';
import { onError } from '@apollo/client/link/error';
import { setContext } from '@apollo/link-context';
import { createAuth0 } from '@auth0/auth0-vue';
import { ApolloClients } from '@vue/apollo-composable';
import FloatingVue from 'floating-vue';
import { createPinia } from 'pinia';
import PrimeVue from 'primevue/config';
import Tailwind from 'primevue/passthrough/tailwind';
import { createApp } from 'vue';

import App from './App.vue';
import { getOperationId, setAuthenticatedUser } from './appInsights';
import router from './router';
import { initSentry, setAuthenticatedUserSentry } from './sentry';
import { useNotificationStore } from './stores/notifications';

const app = createApp(App);

app.use(PrimeVue, { pt: Tailwind });
// Floating Vue provides the VDropdown used by HoverCard.
app.use(FloatingVue);
app.use(createPinia());
app.use(router);
initSentry(app, router);
const auth0 = createAuth0({
  domain: import.meta.env.VITE_AUTH0_DOMAIN,
  clientId: import.meta.env.VITE_AUTH0_CLIENT_ID,
  authorizationParams: {
    redirect_uri: import.meta.env.VITE_AUTH0_CALLBACK_URL,
    audience: import.meta.env.VITE_AUTH0_AUDIENCE,
  },
  cacheLocation: 'localstorage',
});
app.use(auth0);

const { pushNotification } = useNotificationStore();
const errorLink = onError(({ graphQLErrors }) => {
  pushNotification(
    {
      type: 'error',
      title: 'Error',
      message: graphQLErrors?.map((e) => e.message).join(', ') ?? '',
    },
    10000,
  );
});

app.provide(ApolloClients, {
  default: new ApolloClient({
    link: from([
      errorLink,
      setContext(async () => {
        const token = await auth0.getAccessTokenSilently().catch((reason) => {
          if (['login_required', 'consent_required'].includes(reason.error)) {
            auth0.loginWithRedirect();
          }
          throw reason;
        });
        const user = auth0.user.value;
        if (user?.sub) {
          setAuthenticatedUser(user.sub, user.email);
          setAuthenticatedUserSentry(user.sub, user.email);
        }
        return {
          headers: {
            Authorization: `Bearer ${token}`,
            'x-operation-id': getOperationId() ?? '',
          },
        };
      }).concat(
        createHttpLink({
          uri: import.meta.env.VITE_ADMIN_API_URL,
        }),
      ),
    ]),
    cache: new InMemoryCache(),
    connectToDevTools: false,
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'no-cache',
        errorPolicy: 'ignore',
      },
      query: {
        fetchPolicy: 'no-cache',
        errorPolicy: 'all',
      },
    },
  }),
});

app.mount('#app');
